import styled from "styled-components";
import { typographyMixin } from "../../../design-system/tokens/typography";
import { authColors } from "../../../styles/authTokens";
import { duration, easing } from "../../../design-system/tokens/transitions";

/**
 * Auth-flow styling: colours come from `styles/authTokens.ts` (`authColors`),
 * not the app-wide `rawColors`, because the auth screens use the blue primary
 * `#1656CF` where the brand primary is purple. Sizing is in `rem`; the only
 * `px` is the 1px hairline border, per CLAUDE.md.
 */

export const Row = styled.button<{ $selected: boolean }>`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid
    ${({ $selected }) =>
      $selected ? authColors.buttonPrimary : authColors.inputBorder};
  background: ${authColors.white};
  box-shadow: ${authColors.inputShadow};
  cursor: pointer;
  text-align: left;
  transition:
    border-color ${duration.fast} ${easing.smooth},
    background-color ${duration.fast} ${easing.smooth};

  &:hover:not(:disabled) {
    border-color: ${authColors.buttonPrimary};
  }

  /* Keyboard focus must stay visible — the row is the focusable control, the
     checkbox inside it is inert (aria-hidden). */
  &:focus-visible {
    outline: 2px solid ${authColors.buttonPrimary};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

export const Body = styled.span`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0; /* lets the name ellipsize instead of stretching the row */
  flex: 1;
`;

export const Name = styled.span`
  ${typographyMixin("body", 3)}
  color: ${authColors.textHighEmphasis};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const Slug = styled.span`
  ${typographyMixin("caption", 4)}
  color: ${authColors.textMedEmphasis};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/**
 * The Checkbox atom is presentational here: the row owns the click target and
 * the ARIA state, so the checkbox must not swallow pointer events or take its
 * own tab stop.
 */
export const CheckSlot = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  pointer-events: none;
`;
